import React, { useState, useEffect, useRef } from 'react';
import DOMPurify from 'dompurify';
import '../../main-css/chatbox-css/chatbox.css';
import '../../main-css/chatbox-css/reddit.css';
import '../../main-css/chatbox-css/chatboxads.css';
import ChatbotVoice from './ChatbotVoicePage.js';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || process.env.REACT_APP_BACKEND_PROD_URL;

const ChatbotPage = () => {
  const [userMessage, setUserMessage] = useState('');
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [botLoading, setBotLoading] = useState(false);
  const [redditPosts, setRedditPosts] = useState([]);
  const [loadingReddit, setLoadingReddit] = useState(false);
  const [after, setAfter] = useState('');
  const [hasMore, setHasMore] = useState(true);
  const bottomObserverRef = useRef(null); // Ref for observing the transparent tile
  const MAX_CHARACTERS = 500;//User Character Count

  //#region Reddit
  // Fetch Reddit Posts from URL
  const fetchRedditPosts = async () => {
    if (loadingReddit || !hasMore) return;

    setLoadingReddit(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/subreddit/anime?after=${after || ''}`);
      const { posts, nextAfter } = await response.json();

      if (posts.length > 0) {
        setRedditPosts((prevPosts) => [...prevPosts, ...posts]);
        setAfter(nextAfter); // Update after parameter to fetch next batch
        setHasMore(!!nextAfter); // If there's a nextAfter, we have more posts
      } else {
        setHasMore(false); // No more posts to fetch
      }
    } catch (error) {
      console.error('Error fetching Reddit posts:', error);
    } finally {
      setLoadingReddit(false);
    }
  };

  // Second useEffect: Infinite scroll observer
  useEffect(() => {
    if (!bottomObserverRef.current || !hasMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !loadingReddit) {
          fetchRedditPosts(); // Fetch next posts only if not already loading
        }
      },
      { threshold: 0.1 } // Trigger when the entire tile is visible
    );

    observer.observe(bottomObserverRef.current);

    return () => observer.disconnect(); // Cleanup observer on component unmount
  }, [hasMore, loadingReddit]); // Observes changes in loadingReddit and hasMore
//#endregion
  
  //#region Chatbot Frontend Code
  const scrollToBottom = () => {
    const messagesContainer = document.querySelector('.messages');
    if (messagesContainer) {
      messagesContainer.scrollTo({
        top: messagesContainer.scrollHeight,
        behavior: 'smooth', // Smooth scrolling built-in
      });
    }
  };


  // Add this link processing function at the top of your file
  const makeLinksClickable = (text) => {
    if (!text || typeof text !== 'string') return text;

    // Updated pattern to match [SiteName](domain) format
    const linkPattern = /\[([^\]]+)\]\(([^)]+)\)/g;
    
    let processedText = text.replace(linkPattern, (match, siteName, url) => {
      // Add https:// if not present
      const fullUrl = url.startsWith('http') ? url : `https://${url}`;
      // Clean up the URL (remove any trailing punctuation or quotes)
      const cleanUrl = fullUrl.replace(/["\].)]+$/, '');
      
    return `<a href="${cleanUrl}" target="_blank" rel="noopener noreferrer">[${siteName}]</a>`;
    });
  
    return processedText;
  };

  const handleMessageChange = (event) => {
    const input = event.target.value; 
    // Limit characters to MAX_CHARACTERS
    if (input.length <= MAX_CHARACTERS) {
      setUserMessage(input);
    } else {
      alert(`Message too long! Please keep it under ${MAX_CHARACTERS} characters.`);
    }
  };

  //Bot Emotion
  const getEmotionBot = (message) => {
    const emotionMatch = message.match(/\[emotion:(.*?)\]/);
    const emotion = emotionMatch ? emotionMatch[1] : 'neutral';
    const botEmotions = {
      happy:'/Images/Ishtar/IshtarHappy.png',
      sad: '/Images/Ishtar/IshtarSad.png',
      angry: '/Images/Ishtar/IshtarAngryOpen.png',
      surprised: '/Images/Ishtar/IshtarSurprisedOpen.png',
      neutral: '/Images/Ishtar/IshtarNeutral.png'
    };
    return botEmotions[emotion] || botEmotions.neutral;
  };

  const sendMessage = async () => {
    if (!userMessage.trim()) return;
  
    
    setMessages((prevMessages) => [
      ...prevMessages,
      { text: userMessage, sender: 'user' },
    ]);
  
    setBotLoading(true);
    setIsLoading(true);

    try {
      const response = await fetch(`${BACKEND_URL}/chat`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query: userMessage }),
      });
  
      const data = await response.json();
      const responseText = data.response;
  
      // Split response into sentences
      const sentences = responseText.split(/(?<!\w\.\w.)(?<![A-Z][a-z]\.)(?<=\.|\?|!)\s+/);
  
      let groupedSentences = [];
      for (let i = 0; i < sentences.length; i += 3) {
        const pair = sentences.slice(i, i + 3).join(' ').trim();
        const clickablePair = makeLinksClickable(pair);
        const cleanedMessage = cleanMessage(clickablePair);
  
         // Only add message if it's not empty after cleaning
        if (cleanedMessage) {
          groupedSentences.push({
            text: clickablePair,
            sender: 'bot',
            avatarSrc: getEmotionBot(responseText),
            key: `bot-msg-${Date.now()}-${Math.random().toString(36).substr(2, 9)}}`,
            isHTML: true,
          });
        }
      }
  
      setMessages((prevMessages) => [...prevMessages, ...groupedSentences]);
    } catch (error) {
      console.error('Error:', error);
      setMessages((prevMessages) => [
        ...prevMessages,
        { text: "Sorry, I can't think of a response.",
          sender: 'bot',
          avatarSrc: '/Images/Ishtar/IshtarNeutral.png',
        },
      ]);
    } finally {
      setIsLoading(false);
      setBotLoading(false);
      setUserMessage('');
    }
  };
  
  //Clean Message Emotion Tag
  const cleanMessage = (message) => {
    return message.replace(/\[emotion:.*?\]/, '').trim();
  };
  
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault(); // Prevents default behavior of the Enter key (e.g., adding a new line)
      if(!isLoading) sendMessage(); // Calls sendMessage when Enter is pressed
    }
  };

  useEffect(() => {
    const loadChatHistory = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/chat/history`, {
          method: 'GET',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
        });
  
        if (!response.ok) {
          console.error('Failed to load chat history:', response.status, response.statusText);
          return setMessages([]); 
        }
  
        const data = await response.json();
  
        if (!Array.isArray(data.chatHistory) || data.chatHistory.length === 0) {
          console.log('No chat history found or empty history array');
          return setMessages([]); 
        }
  
        const processedHistory = data.chatHistory
          .map((message, index) => {
            if (!message || !message.role || typeof message.content === 'undefined') {
              console.error('Invalid message structure:', message);
              return null; // Skip invalid messages
            }
  
            const sender = message.role === 'assistant' ? 'bot' : 'user';
            return {
              text: String(message.content), // Ensure content is always a string
              sender: sender,
              key: `history-msg-${index}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
              isHTML: sender === 'bot',
            };
          })
          .filter(Boolean); // Remove null values from the array
  
        if (processedHistory.length) {
          setMessages(processedHistory);
        } else {
          console.warn('No valid messages found in chat history');
          setMessages([]);
        }
      } catch (error) {
        console.error('Error loading chat history:', error);
        setMessages([]);
      }
    };
  
    loadChatHistory();
  }, []);


//#endregion 


  //#region Ads Code
 
 
  //#endregion
 
  //#region FrontEnd UI
  return (
    <div className="chatbot-page">
      <div className="chatbot-container">
        <div className="chat-box">
          <div className="messages">
          {messages.map((msg, index) => {
            const isFirstInGroup = index === 0 || messages[index - 1]?.sender !== msg.sender;
            const cleanedText = msg.sender === 'bot' ? cleanMessage(msg.text) : msg.text;
            
            return (
              <div key={msg.key} className={`message ${msg.sender === 'user' ? 'user' : 'bot'}`}>
                {/* Keep alignment consistent by rendering a placeholder if avatar is not shown */}
                {msg.sender === 'bot' && (
                  isFirstInGroup ? (
                    <img src= {msg.avatarSrc || "/Images/Ishtar/IshtarNeutral.png"} alt="Bot Avatar" className="bot-avatar" />
                  ) : (
                    <div className="bot-avatar-placeholder" />
                  )
                )}
                <div className="message-bubble" dangerouslySetInnerHTML={{ 
                __html: DOMPurify.sanitize(makeLinksClickable(cleanedText), {
                  ADD_ATTR: ['target', 'rel'],
                  ALLOWED_TAGS: ['a', 'p', 'br', 'strong', 'em'],
                  ALLOWED_ATTR: ['href', 'target', 'rel']
                })
              }}></div>
              </div>
            );
          })}
            {botLoading &&(
              <div className = "message bot">
                 <img src="/Images/Ishtar/IshtarNeutral.png" alt="Bot Avatar" className="bot-avatar" />
                 <div className="message-bubble">
                  <span className="dot-one">.</span><span className="dot-two">.</span><span className="dot-three">.</span>
                  </div>
                </div>
            )}
          </div>
          <div className="input-container">
            {/*<ChatbotVoice 
              onMessageRecognized={setUserMessage} 
              BACKEND_URL={BACKEND_URL}
              setMessages={setMessages}
              setIsLoading={setIsLoading}
              setBotLoading={setBotLoading}
              isLoading={isLoading}
              botLoading={botLoading}
            />*/}
            <textarea
              value={userMessage}
              onChange={handleMessageChange}
              onKeyDown={handleKeyDown}
              placeholder="Ask something about anime..."
              className="chat-input"
              readOnly={isLoading}
            />
            <div className="button-container">
              <button 
                onClick={sendMessage} 
                className={`send-button ${isLoading ? 'sending' : ''}`}
                disabled={isLoading}
              >
                {isLoading ? 'Sending...' : 'Send'}
              </button>
              <div 
                className={`char-count ${
                  userMessage.length > MAX_CHARACTERS * 0.8 
                    ? userMessage.length > MAX_CHARACTERS * 0.9 
                      ? 'danger' 
                      : 'warning'
                    : ''
                }`}
              >
                {`${userMessage.length}/${MAX_CHARACTERS}`}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="right-side-container">
      <div className="reddit-container">
          <h2 className="reddit-title">Reddit</h2>
          <div className="reddit-tiles">
            {redditPosts.map((post, index) => (
              <div key={index} className="reddit-tile">
                {post.thumbnail &&
                  post.thumbnail !== 'self' &&
                  post.thumbnail !== 'default' &&
                  post.thumbnail.startsWith('http') &&
                  !post.is_video && ( // Ensure it's a valid URL
                  <img src={post.thumbnail} alt="Reddit Thumbnail" className="reddit-thumbnail" />
                )}
                <div className="reddit-text">
                  <a href={post.url} target="_blank" rel="noopener noreferrer" className="reddit-link">
                    {post.title}
                  </a>
                  <p className="reddit-author">Author: {post.author}</p>
                  <p className="reddit-score">Score: {post.score}</p>
                </div>
              </div>
            ))}

            {/* Transparent Reddit Button Tile */}
            <div
              ref={bottomObserverRef}
              className="reddit-tile transparent-tile"
              style={{
                backgroundColor: 'rgba(0, 0, 0, 0.1)',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <span style={{ color: 'gray' }}>Loading more...</span>
            </div>
          </div>
          {loadingReddit && <p>Loading...</p>}
          {!hasMore && <p>No more posts available</p>}
        </div>

        <div className="ads-container">
          <a 
              href="https://play.google.com/store/apps/details?id=com.ALSQ.StayUp" 
              target="_blank" 
              rel="noopener noreferrer"
          >
            <img src="/Images/StayUpThumn.jpg" alt="ADS Thumbnail" className='ads-embed'></img>      
          </a>
        </div>
      </div>
      
      <div className="disclaimer">
      <img src="/Images/AnimeBibleLogoTP.png" alt="Anime Logo" className="disclaimer-image" />
        Ishtar can make mistakes!        
      </div>
    </div>
  );
  //#endregion
};

export default ChatbotPage;

